/* ============================================================================
 * zivu.eu — ecommerce analytics helpers
 *
 * Each helper builds flat event params and hands them to window.zivuTrack()
 * (defined in /public/tracking-loader.js), which fans the event out to every
 * loaded + consented tag: GA4 (gtag), Google Ads (purchase conversion), Meta
 * Pixel (mapped FB event), Bing, TikTok, Pinterest, Yandex.
 *
 * params are flat and superset-shaped so every platform finds what it needs:
 *   value, currency            -> GA4, Ads, Meta
 *   items[]                    -> GA4 ecommerce
 *   content_ids[], content_type-> Meta dynamic ads
 *   transaction_id             -> GA4 + Ads purchase dedup
 *
 * Never throws — analytics must never break the app. Safe no-op if the loader
 * hasn't defined zivuTrack yet (falls back to a raw dataLayer push).
 * ==========================================================================*/

const CURRENCY = 'EUR';

function emit(eventName, params, eventId) {
  try {
    if (typeof window === 'undefined') return;
    if (typeof window.zivuTrack === 'function') {
      window.zivuTrack(eventName, params || {}, eventId);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
    }
  } catch (e) {
    /* analytics must never break the app */
  }
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function listingToItem(listing, quantity) {
  if (!listing) return null;
  const fitment = listing.fitment || {};
  return {
    item_id: listing.listing_id || listing.id || listing.sku || undefined,
    item_name: (listing.title || '').slice(0, 100) || undefined,
    item_brand: fitment.make_name || listing.manufacturer || undefined,
    item_category: listing.category_name || undefined,
    price: num(listing.price),
    quantity: quantity || 1,
  };
}

function idsOf(items) {
  return (items || []).map((i) => i && i.item_id).filter(Boolean);
}

export function trackViewItem(listing) {
  if (!listing) return;
  const items = [listingToItem(listing, 1)].filter(Boolean);
  emit('view_item', {
    currency: CURRENCY,
    value: num(listing.price) || 0,
    items,
    content_ids: idsOf(items),
    content_type: 'product',
  });
}

export function trackAddToCart(listing, quantity = 1) {
  if (!listing) return;
  const items = [listingToItem(listing, quantity)].filter(Boolean);
  const price = num(listing.price) || 0;
  emit('add_to_cart', {
    currency: CURRENCY,
    value: price * (quantity || 1),
    items,
    content_ids: idsOf(items),
    content_type: 'product',
  });
}

export function trackBeginCheckout(cart) {
  const rawItems = (cart && cart.items) || [];
  const items = (Array.isArray(rawItems) ? rawItems : []).map((it) => ({
    item_id: it.listing_id || it.id || it.sku || undefined,
    item_name: (it.title || '').slice(0, 100) || undefined,
    price: num(it.price),
    quantity: it.quantity || it.qty || 1,
  }));
  emit('begin_checkout', {
    currency: CURRENCY,
    value: num(cart && (cart.subtotal != null ? cart.subtotal : cart.total)) || 0,
    items,
    content_ids: idsOf(items),
    content_type: 'product',
  });
}

export function trackViewCart(cart) {
  const rawItems = (cart && cart.items) || [];
  const items = (Array.isArray(rawItems) ? rawItems : []).map((it) => ({
    item_id: it.listing_id || it.id || it.sku || undefined,
    item_name: (it.title || '').slice(0, 100) || undefined,
    price: num(it.price),
    quantity: it.quantity || it.qty || 1,
  }));
  if (!items.length) return;
  emit('view_cart', {
    currency: CURRENCY,
    value: num(cart && (cart.subtotal != null ? cart.subtotal : cart.total)) || 0,
    items,
    content_ids: idsOf(items),
    content_type: 'product',
  });
}

export function trackPurchase(order) {
  if (!order) return;
  const rawItems = order.items || order.order_items || order.line_items || [];
  const items = (Array.isArray(rawItems) ? rawItems : []).map((it) => ({
    item_id: it.listing_id || it.id || it.sku || undefined,
    item_name: (it.title || it.name || '').slice(0, 100) || undefined,
    item_brand: (it.fitment && it.fitment.make_name) || it.manufacturer || undefined,
    price: num(it.price != null ? it.price : it.unit_price),
    quantity: it.quantity || it.qty || 1,
  }));
  const txnId = order.order_id || order.id || undefined;
  const value = num(order.total != null ? order.total : order.amount) || 0;
  // Meta requires Purchase value > 0. Skip 0-value orders (fully-discounted/test)
  // so they don't trigger "Fix price data for Purchase events" in Events Manager.
  if (value <= 0) return;
  emit('purchase', {
    transaction_id: txnId,
    value,
    currency: order.currency || CURRENCY,
    // 2026-08-05: `delivery_price` has never existed on an order document — the
    // delivery charge is persisted as `shipping_cost`. Both earlier keys missed,
    // so GA4 received shipping: 0 on every purchase while `value` still included
    // the delivery charge. Third fallback added; keep all three for back-compat.
    shipping: num(
      order.shipping != null
        ? order.shipping
        : (order.delivery_price != null ? order.delivery_price : order.shipping_cost)
    ),
    // Reported so a 100%-off test order reconciles: a €3.20 charge containing a
    // €140.78 item only makes sense once the coupon is visible in the event.
    coupon: order.coupon_code || undefined,
    items,
    content_ids: idsOf(items),
    content_type: 'product',
  }, txnId); // event_id = order_id → dedupes the browser pixel with the server CAPI event
}
